/**
 * File: apps/qbench-cp/reactapp/src/shared/configs/runtimeConfig.js
 *
 * Purpose: The portal's single source of configuration, read where the bundle runs rather than
 *     inlined where it was built.
 *
 * Scope: The only module in the portal permitted to name process.env.REACT_APP_*.
 *
 * Tags: customer-portal
 *
 * Overview: Trunk-Based Development requirement 60 says a deployable's artifact carries no
 *     environment-specific value, so that one artifact serves every environment. Under Create
 *     React App, process.env in application code is a literal object baked in at build time, so
 *     any REACT_APP_ value a build is given is frozen into the bundle. This module is the seam
 *     that keeps such a value out of it: the deploy writes a config.js into the bucket beside the bundle, a classic script tag in
 *     public/index.html runs it before the deferred bundle, and it assigns window.__APP_CONFIG__.
 *
 *     Values fall back to process.env when the window carries none, which is what keeps `npm
 *     start` a single step and the jest suite unchanged.
 *
 *     Two things the module owns that the call sites used to repeat. The API base defaulted to
 *     "/portal/api" in ten separate places, so ten places could disagree; it defaults here once.
 *     And the stub switches were each compared against the string 'true' at their own site, so a
 *     hand-written config.js could not use a real boolean; the comparison lives here, and both a
 *     boolean and the string are read the same way.
 *
 *     Resolved on every call rather than frozen at module load, because several call sites read
 *     at module load themselves and a frozen value would pin whatever the first importer saw.
 *
 * Exports: getRuntimeConfig, which resolves the configuration on each call.
 */

/** The portal's own API path. Correct in every environment, because it is relative to the app. */
const DEFAULT_BASE_URL = '/portal/api';

/** What config.js assigns. Absent here means "not configured", never "configured as nothing". */
const deployedConfig = () => (typeof window === 'undefined' ? undefined : window.__APP_CONFIG__) || {};

/**
 * Reads a switch the same way whether it arrives as a boolean from config.js or as the string
 * 'true' from an env file. Anything else, including the string 'false', is off.
 */
const asFlag = (deployedValue, builtValue) => {
  const value = deployedValue === undefined ? builtValue : deployedValue;
  return value === true || value === 'true';
};

/**
 * Resolves configuration: the window first, then the build-time value, then the stated default.
 */
export const getRuntimeConfig = () => {
  const deployed = deployedConfig();

  return {
    baseUrl: deployed.baseUrl || process.env.REACT_APP_BASE_URL || DEFAULT_BASE_URL,
    stubsEnabled: asFlag(deployed.stubsEnabled, process.env.REACT_APP_STUBS_ENABLED),
    loggedOutStub: asFlag(deployed.loggedOutStub, process.env.REACT_APP_LOGGED_OUT_STUB),
  };
};
